import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IPricingAdjustResponse } from "@miplatform/types";
import { DEFAULT_VALUES } from "../constants";

export const usePricingAdjustmentsViewModel = ({
  planId,
  accountId,
  reset,
  setOpenAddAdjustment,
}: UsePricingAdjustmentViewModelProps) => {
  const [pricingDetails, setPricingDetails] =
    useState<IPricingAdjustResponse | null>(null);

  const stateManager = useComponentState();

  const getPricingDetails = () => {
    stateManager.run(async () => {
      const response =
        await homeService.pricing.pricingAdjustment.getPriceDetails({
          planId,
          accountId,
        });
      if (response.status === HTTP_STATUSES.OK) {
        setPricingDetails(response.data);
      }
    });
  };

  const createPricingAdjustments = (data: AdjustmentRequestPayload) => {
    stateManager.run(async () => {
      const response = await homeService.pricing.pricingAdjustment.create({
        accountId,
        data,
        toastMessageConfig: {
          success: {
            text: `Successfully created adjustment: ${data.label}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        getPricingDetails();
        reset(DEFAULT_VALUES);
        setOpenAddAdjustment(false);
      }
    });
  };

  const updatePricingAdjustments = (
    data: AdjustmentRequestPayload,
    pricingAdjustmentId: string
  ) => {
    stateManager.run(async () => {
      const response = await homeService.pricing.pricingAdjustment.update({
        data,
        planId,
        pricingAdjustmentId,
        toastMessageConfig: {
          success: {
            text: `Successfully updated adjustment: ${data.label}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        getPricingDetails();
      }
    });
  };

  const deletePricingAdjustments = (
    data: Partial<AdjustmentRequestPayload>
  ) => {
    stateManager.run(async () => {
      const response = await homeService.pricing.pricingAdjustment.delete({
        planId,
        pricingAdjustmentId: data.id,
        modifiedOn: data.modifiedOn,
        toastMessageConfig: {
          success: {
            text: `Successfully deleted adjustment: ${data.label}.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        getPricingDetails();
      }
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    getPricingDetails,
    createPricingAdjustments,
    updatePricingAdjustments,
    deletePricingAdjustments,
    pricingDetails,
    pricingAdjustments: pricingDetails?.adjustments ?? [],
  };
};

interface UsePricingAdjustmentViewModelProps {
  planId: string;
  accountId: string;
  reset: (values?: any) => void;
  setOpenAddAdjustment: (open: boolean) => void;
}

export interface AdjustmentRequestPayload {
  id?: string;
  label: string;
  amount: number | string;
  note?: string | null;
  type: number | string | null;
  planId: string | null;
  optionId?: string | null;
  requirementId?: string | null;
  planMaterialId?: string | null;
  categoryId?: string | null;
  floorId?: string | null;
  dropTypeId?: string | null;
  planAreaId?: string | null;
  modifiedOn?: string | null;
}
